"use client";

import React, { useState, useEffect } from "react";
import dynamic from "next/dynamic";
import { useKeenSlider } from "keen-slider/react";
import { ServicesIndustryBlock } from "../index";

import Section from "@/Components/LayoutComponents/Section";
import { toWebp } from "@/utils/common";
const ServicesIndustryCard = dynamic(() => import("./ServicesIndustryCard"), {
  ssr: false,
});

interface ServicesIndustryCardProps {
  data: ServicesIndustryBlock;
}

const ServicesIndustry: React.FC< ServicesIndustryCardProps> = ({ data  }) =>  {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    loop: true,
    slides: {
      perView: 1,
      spacing: 15,
    },
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
    created() {
      setLoaded(true);
    },
  });

  // 🌀 Auto-play effect
  useEffect(() => {
    const interval = setInterval(() => {
      if (instanceRef.current) {
        instanceRef.current.next();
      }
    }, 3000); // Slide every 3 seconds

    return () => clearInterval(interval); // Cleanup
  }, [instanceRef]);

  const getSafeLink = (card: any) => {
    const href = card?.link?.[0]?.href;
    return typeof href === "string" && href.trim() !== "" ? href : "";
  };

  const getSafeLabel = (card: any) => {
    const label = card?.link?.[0]?.label;
    return typeof label === "string" ? label : "";
  };

  return (
    // <Section className="relative bg-[url(/images/enterprise-bg.png)] bg-size-[60%] bg-no-repeat bg-left-top">
    <Section className="xl:py-[100px] md:py-[95px] py-[56px] relative bg-[url(/images/bg-block.png)]  bg-size-[100%] bg-top sm:bg-[url(/images/enterprise-bg.png)] sm:bg-size-[60%] bg-no-repeat sm:bg-left-top">
      <div className="absolute z-[-1] top-0 right-0 w-full h-full bg-[linear-gradient(96.18deg,_#E6F5FF_0%,_#5AA7DF_100%)]">
        <div className="w-full h-full bg-[linear-gradient(0deg,_rgba(255,_255,_255,_0.8),_rgba(255,_255,_255,_0.8))]" />
      </div>

      <div className="container">
        <div className="text-center">
          <h2 className="text-heading-1 max-w-[1230px] mx-auto font-bold text-primary-text">
            {data?.title || ""}
          </h2>
          <p className="text-subtitle-1 font-regular max-w-[1230px] mx-auto text-primary-text mt-5">
            {data?.sub_title || ""}
          </p>
        </div>

        {/* Desktop Cards */}
        <div className="md:grid hidden grid-cols-3 gap-[30px] xl:mt-10 mt-8">
          {data?.cards?.map((card: any) => (
            <ServicesIndustryCard
              key={card.id}
              backgroundImage={toWebp(card.image?.data?.attributes?.url)}
              title={card.title}
              description={card.description}
              buttonLabel={getSafeLabel(card)}
              buttonLink={getSafeLink(card)}
            />
          ))}
        </div>

        {/* Mobile Slider */}
        <div className="md:hidden block xl:mt-10 mt-8">
          <div ref={sliderRef} className="keen-slider">
            {data?.cards?.map((card: any) => (
              <div className="keen-slider__slide" key={card.id}>
                <ServicesIndustryCard
                  backgroundImage={toWebp(card.image?.data?.attributes?.url)}
                  title={card.title}
                  description={card.description}
                  buttonLabel={getSafeLabel(card)}
                  buttonLink={getSafeLink(card)}
                />
              </div>
            ))}
          </div>

          {/* Dots */}
          {loaded && instanceRef.current && (
            <div className="flex justify-center items-center gap-2 px-12 py-6 w-[67%] bg-transparent mx-auto">
              {Array.from({
                length:
                  instanceRef?.current?.track?.details?.slides?.length || 0,
              }).map((_, idx) => (
                <button
                  aria-label="dot"
                  key={idx}
                  onClick={() => instanceRef.current?.moveToIdx(idx)}
                  className={`p-0 relative overflow-hidden ${
                    currentSlide === idx
                      ? "flex-1 min-w-[60px] w-16 h-1 transition-none bg-background"
                      : "flex-none w-4 h-1 bg-background"
                  }`}
                >
                  <span
                    className={`absolute top-0 left-0 w-full h-1 ${
                      currentSlide === idx
                        ? "bg-gradient-to-r from-[#8ABC00] to-[#0086E6] transition-all duration-[2000ms] delay-[200ms]"
                        : "left-[-100%] transition-none"
                    }`}
                  ></span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Section>
  );
};
export default ServicesIndustry;